import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ServiceStatus } from '@upme/contracts';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { ImportNhatrangGuideButton } from '../../../features/services/components/import-nhatrang-guide-button';
import { ServicesTable } from '../../../features/services/components/services-table';
import { listServicesOptions } from '../../../features/services/queries';
import { PageHeader } from '../../../shared/components/page-header';
import { Route as ServicesRoute } from './route';

const ANY = '__any__';

const SERVICE_STATUS_LABEL: Record<ServiceStatus, string> = {
  DRAFT: 'Черновик',
  PENDING_MODERATION: 'На модерации',
  PUBLISHED: 'Опубликована',
  ARCHIVED: 'В архиве',
};

const STATUSES: ServiceStatus[] = ['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'ARCHIVED'];

export const Route = createFileRoute('/_authenticated/services/')({
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(listServicesOptions({ status: deps.status })),
  component: ServicesIndexPage,
});

function ServicesIndexPage() {
  const search = ServicesRoute.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(listServicesOptions({ status: search.status }));

  const handleStatusChange = (value: string) => {
    navigate({
      to: '/services',
      search: () => {
        if (value === ANY) return {};
        return { status: value as ServiceStatus };
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Услуги"
        description="Объявления исполнителей. Здесь можно отредактировать категории, статус и описание услуги."
        actions={<ImportNhatrangGuideButton />}
      />

      <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-3">
        <div>
          <Label className="text-xs">Статус</Label>
          <Select value={search.status ?? ANY} onValueChange={handleStatusChange}>
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {SERVICE_STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ServicesTable services={data.items} />
    </div>
  );
}
